// Figma: https://www.figma.com/design/iYlbYhvyJjh7X00UAcqCWt/About-page?node-id=13-6064
import { Box, Center, Divider, Flex, Image, Link, Text } from "@chakra-ui/react";
import type { LinkProps } from "@chakra-ui/react";
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { aboutAsset } from "../aboutAssets";

const gcMark = aboutAsset("gc-mark.svg");

// Greetings that pop out of the GC mark on hover — a nod to GlobalComix's
// global audience. Random each hover, no immediate repeats.
const GREETINGS = [
  "Hi!",
  "¡Hola!",
  "Bonjour!",
  "こんにちは",
  "你好",
  "Hallo!",
  "Ciao!",
  "Olá!",
  "Привет!",
  "안녕!",
  "Namaste",
  "Merhaba!",
  "Hej!",
  "Hoi!",
  "مرحبا",
  "Shalom!",
  "Xin chào!",
  "Aloha!",
  "Kia ora!",
  "Sawadee!",
];

function GCMarkWithGreeting() {
  // Pre-randomize so even the first hover isn't always "Hi!".
  const initialIndex = useRef(Math.floor(Math.random() * GREETINGS.length));
  const [greeting, setGreeting] = useState(() => GREETINGS[initialIndex.current]);
  const lastIndexRef = useRef(initialIndex.current);

  const pickNext = () => {
    let next = lastIndexRef.current;
    while (next === lastIndexRef.current && GREETINGS.length > 1) {
      next = Math.floor(Math.random() * GREETINGS.length);
    }
    lastIndexRef.current = next;
    setGreeting(GREETINGS[next]);
  };

  // Queue the next greeting once the bubble has fully hidden. This avoids a
  // visible layout shift: if we change the text on mouseEnter, the hover
  // transition starts with the OLD text's width and then reflows when React
  // re-renders — making the bubble appear to jump off-center.
  const handleMouseLeave = () => {
    window.setTimeout(pickNext, 350); // slightly longer than the 300ms exit.
  };

  return (
    <Box
      role="group"
      position="relative"
      display="inline-flex"
      w="30px"
      h="30px"
      alignItems="center"
      justifyContent="center"
      onMouseLeave={handleMouseLeave}
    >
      {/* Zero-width anchor pinned above the mark's center. Flex-centering the
          bubble inside a 0-width container keeps the bubble centered on the
          anchor regardless of text width — no translateX math, no jitter
          when the text changes. */}
      <Center
        position="absolute"
        bottom="calc(100% + 8px)"
        left="50%"
        w="0"
        pointerEvents="none"
      >
        <Box
          flex="none"
          opacity={0}
          transform="scale(0.5)"
          transformOrigin="center bottom"
          transition="opacity 0.25s ease, transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
          bg="dune.dune"
          color="charcoal.charcoal"
          fontSize="11px"
          fontWeight="700"
          px="8px"
          py="4px"
          borderRadius="8px"
          whiteSpace="nowrap"
          sx={{
            "&::after": {
              content: '""',
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "5px solid var(--chakra-colors-dune-dune)",
            },
          }}
          _groupHover={{ opacity: 1, transform: "scale(1)" }}
        >
          {greeting}
        </Box>
      </Center>
      <Image src={gcMark} alt="GlobalComix" w="30px" h="auto" />
    </Box>
  );
}

function LegalLink({ children, ...props }: LinkProps & { children: ReactNode }) {
  return (
    <Link
      {...props}
      fontSize="12px"
      fontWeight="400"
      color="neutral.500"
      textDecoration="none"
      position="relative"
      display="inline-block"
      _hover={{ color: "neutral.400" }}
      sx={{
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-2px",
          left: "0",
          width: "0%",
          height: "1px",
          backgroundColor: "var(--chakra-colors-neutral-400)",
          transition: "width 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)",
        },
        "&:hover::after": {
          width: "100%",
        },
      }}
    >
      {children}
    </Link>
  );
}

export function FooterSection() {
  return (
    <Box
      bg="charcoal.600"
      px="24px"
      pb="40px"
    >
      <Box maxW="960px" mx="auto">
      {/* Divider (13:6065) */}
      <Divider borderColor="rgba(255,255,255,0.2)" mb="32px" />

      {/* Content (13:6066) */}
      <Flex
        justify="space-between"
        align="center"
        direction={{ base: "column", md: "row" }}
        gap="24px"
      >
        {/* Brand (13:6067) */}
        <Flex align="center" gap="12px">
          {/* GC Mark (13:6068) — hover for a greeting in a random language. */}
          <GCMarkWithGreeting />
          {/* Copyright (13:6071) */}
          <Text fontSize="12px" fontWeight="400" color="neutral.500">
            &copy; 2019 - 2026 GlobalComix
          </Text>
        </Flex>

        {/* Legal links (13:6072) */}
        <Flex gap="24px">
          {/* Privacy Policy (13:6073) */}
          <LegalLink href="https://globalcomix.com/privacy-policy" isExternal>
            Privacy Policy
          </LegalLink>
          {/* Terms of Service (13:6074) */}
          <LegalLink href="https://globalcomix.com/terms-of-use" isExternal>
            Terms of Service
          </LegalLink>
        </Flex>
      </Flex>
      </Box>
    </Box>
  );
}
